/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Box, Button, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import FileFormField from '../../../components/form/FileFormField';
import LoadingOverlayText from '../../../components/LoadingOverlay/LoadingOverlayText';
import c from '../../../constant';
import DateTimeFormField from '../../../utils/DateTimeFormField';
import useStore from '../../../store';
import DropDownField from '../../../components/form/DropDownField';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const CREATE_MUTATION = gql`
	mutation create(
		$position: PositionStatusPositionEnum!
		$dateOfLastPosition: DateTime!
		$rank: PositionStatusRankEnum!
		$dateOfLastRank: DateTime!
		$department: String!
		$office: String!
		$userId: String!
		$organisation: String!
		$attachmentFile: Upload
		$attachmentFileSize: Float
	) {
		createPositionStatus(
			input: {
				position: $position
				dateOfLastPosition: $dateOfLastPosition
				rank: $rank
				userId: $userId
				dateOfLastRank: $dateOfLastRank
				department: $department
				organisation: $organisation
				office: $office
			}
			attachmentFile: $attachmentFile
			attachmentFileSize: $attachmentFileSize
		) {
			id
			position
			dateOfLastPosition
			rank
			dateOfLastRank
			organisation
			department
			office
		}
	}
`;

const QUERY_POSITION_STATUS_BY_ID = gql`
	query getPositionStatusById($id: String!) {
		getPositionStatusById(id: $id) {
			id
			no
			userId
			rank
			position
			dateOfLastRank
			dateOfLastPosition
			office
			department
			attachmentFile
		}
	}
`;

const MUTATION_UPDATE_POSITION_STATUS = gql`
	mutation updatePositionStatus($id: String!, $input: PositionStatusUpdateInput!, $attachmentFile: Upload, $attachmentFileSize: Float) {
		updatePositionStatus(id: $id, input: $input, attachmentFile: $attachmentFile, attachmentFileSize: $attachmentFileSize)
	}
`;

const validationSchema = Yup.object().shape({
	position: Yup.string().required('required'),
	dateOfLastPosition: Yup.date().required('required'),
	rank: Yup.string().required('required'),
	dateOfLastRank: Yup.date().required('required'),
	department: Yup.string(),
	office: Yup.string(),
	attachmentFile: Yup.mixed().test('fileSize', 'over limit', (value) => {
		if (value) {
			return value?.size <= c.maxFileSize / 20;
		}

		return true;
	}),
});

function PositionStatusAdd({ id, onCloseModal, isEdit = false }) {
	moment.locale('en-gb');
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const userId = urlParams.get('userId') ? urlParams.get('userId') : null;
	const history = useHistory();
	const toast = useToast();
	const { t } = useTranslation();
	const [createMutation, { loading }] = useMutation(CREATE_MUTATION);
	const [updatePositionStatus, { loading: loadingUpdatePositionStatus }] = useMutation(MUTATION_UPDATE_POSITION_STATUS);
	const [loadPositionStatusById, { data: editData }] = useLazyQuery(QUERY_POSITION_STATUS_BY_ID, {
		fetchPolicy: 'network-only',
	});

	useEffect(() => window.scrollTo(0, 0), []);

	useEffect(() => {
		if (isEdit && id) {
			loadPositionStatusById({
				variables: { id },
			});
		}
	}, [isEdit, id]);

	function capitalizeRank(rank) {
		if (!rank) {
			return '';
		}

		return String(rank).toUpperCase();
	}

	const POSITION_MAP = {
		1: 'DIRECTOR_GENERAL',
		2: 'DEPUTY_DIRECTOR_GENERAL',
		3: 'HEAD_OF_DEPARTMENT',
		4: 'DEPUTY_HEAD_OF_DEPARTMENT',
		5: 'HEAD_OF_OFFICE',
		6: 'DEPUTY_HEAD_OF_OFFICE',
		7: 'STATUTE_OFFICER',
		8: 'INTERNSHIP_OFFICER',
	};

	const OFFICE_OPTIONS_BY_DEPARTMENT = {
		D_G_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យរដ្ឋបាល' : 'Administrative', value: 'office1' },
			{ label: lang === 'kh' ? 'ការិយាល័យគណនេយ្យ' : 'Accounting', value: 'office2' },
			{ label: lang === 'kh' ? 'ការិយាល័យធនធានមនុស្ស' : 'Human Resource', value: 'office3' },
			{ label: lang === 'kh' ? 'ការិយាល័យទំនាក់ទំនងសាធារណៈ' : 'Public Relation', value: 'office4' },
		],

		D_L_L_A: [
			{ label: lang === 'kh' ? 'ការិយាល័យគ្រប់គ្រងអាជ្ញាបណ្ណ' : 'Licensing', value: 'office5' },
			{ label: lang === 'kh' ? 'ការិយាល័យចុះបញ្ជីអន្តរការី' : 'Intermediary Registration', value: 'office6' },
			{ label: lang === 'kh' ? 'ការិយាល័យនីតិកម្ម និងផ្សះផ្សាវិវាទ' : 'Legal and Mediation', value: 'office7' },
		],

		D_S: [
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurer', value: 'office8' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងទូទៅ' : 'General Insurer', value: 'office9' },
			{
				label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យក្រុមហ៊ុនធានារ៉ាប់រងខ្នាតតូច​ និង​អន្តរការីធានារ៉ាប់រង' : 'Micro Insurer',
				value: 'office10',
			},
			{ label: lang === 'kh' ? 'ការិយាល័យបទប្បញ្ញត្តិប្រុងប្រយ័ត្ន' : 'Prudential', value: 'office11' },
		],

		D_M_D: [
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងអាយុជីវិត' : 'Life Insurance Product', value: 'office12' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងទូទៅ' : 'General Insurance Product', value: 'office13' },
			{ label: lang === 'kh' ? 'ការិយាល័យផលិតផលធានារ៉ាប់រងខ្នាតតូច' : 'Micro Insurance Product', value: 'office14' },
			{ label: lang === 'kh' ? 'ការិយាល័យត្រួតពិនិត្យប្រតិបត្តិការទីផ្សារ' : 'Market Conduct Supervision', value: 'office15' },
		],

		D_R_T_C: [
			{ label: lang === 'kh' ? 'ការិយាល័យស្រាវជ្រាវ' : 'Research', value: 'office16' },
			{ label: lang === 'kh' ? 'ការិយាល័យបណ្តុះបណ្តាល' : 'Training', value: 'office17' },
			{ label: lang === 'kh' ? 'ការិយាល័យសហប្រតិបត្តិការ' : 'Cooperation', value: 'office18' },
			{ label: lang === 'kh' ? 'ការិយាល័យបច្ចេកវិទ្យាធានារ៉ាប់រង' : 'InsurTech', value: 'office19' },
		],
	};

	function normalizePosition(position) {
		if (!position) return '';

		const value = String(position);

		return POSITION_MAP[value] || value;
	}

	async function submit(values) {
		const createVariables = {
			position: values?.position ? normalizePosition(values?.position) : '',
			dateOfLastPosition: values?.dateOfLastPosition,
			rank: values?.rank ? String(values?.rank) : '',
			dateOfLastRank: values?.dateOfLastRank,
			organisation: lang === 'en' ? 'Insurance Regulator of Cambodia' : 'និយ័តករធានារ៉ាប់រងកម្ពុជា',
			department: values?.department || '',
			office: values?.office || '',
			attachmentFile: values?.attachmentFile ? values?.attachmentFile : null,
			attachmentFileSize: values?.attachmentFile?.size ? values?.attachmentFile?.size : null,
			userId: userId,
		};

		const updateVariables = {
			id,
			input: {
				position: normalizePosition(values?.position),
				dateOfLastPosition: values?.dateOfLastPosition,
				rank: values?.rank ? capitalizeRank(values?.rank) : 'N/A',
				dateOfLastRank: values?.dateOfLastRank,
				organisation: lang === 'en' ? 'Insurance Regulator of Cambodia' : 'និយ័តករធានារ៉ាប់រងកម្ពុជា',
				department: values?.department || 'N/A',
				office: values?.office || 'N/A',
			},
			attachmentFile: values?.attachmentFile || null,
			attachmentFileSize: values?.attachmentFile?.size || null,
		};

		try {
			if (isEdit && id) {
				await updatePositionStatus({
					variables: updateVariables,
				});

				toast({
					title: t('Position status updated successfully'),
					status: 'success',
					isClosable: true,
					duration: 5000,
					position: 'top-right',
				});
			} else {
				const res = await createMutation({
					variables: createVariables,
				});

				const createdId = res?.data?.createPositionStatus?.id;

				if (!createdId) {
					throw new Error('POSITION_STATUS_NOT_CREATED');
				}

				toast({
					title: t('Position status created successfully'),
					status: 'success',
					isClosable: true,
					duration: 5000,
					position: 'top-right',
				});
			}

			if (onCloseModal) {
				onCloseModal();
			}

			if (!isEdit) {
				history.push(`/position-status?userId=${userId}`);
			}
		} catch (error) {
			toast({
				title: t(error?.message ? error?.message : 'something went wrong'),
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'bottom-right',
			});
		}
	}

	const record = editData?.getPositionStatusById;

	const initialValues = {
		position: record?.position ? normalizePosition(record?.position) : '',
		dateOfLastPosition: record?.dateOfLastPosition ? new Date(record?.dateOfLastPosition) : new Date(),
		rank: record?.rank ? capitalizeRank(record?.rank) : '',
		dateOfLastRank: record?.dateOfLastRank ? new Date(record?.dateOfLastRank) : new Date(),
		organisation: lang === 'en' ? 'Insurance Regulator of Cambodia' : 'និយ័តករធានារ៉ាប់រងកម្ពុជា',
		department: record?.department || '',
		office: record?.office || '',
		attachmentFile: null,
	};

	console.log(editData?.getPositionStatusById);

	return (
		<>
			<Box w="100%" bg="white" borderRadius={c.borderRadius} ml="1px" pb="32px">
				<Box maxW="510px">
					<Formik key={isEdit ? record?.id : 'create'} initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit}>
						{(props) => {
							const { values } = props;
							const hideOfficeDepartment = values?.position === 'DIRECTOR_GENERAL' || values?.position === 'DEPUTY_DIRECTOR_GENERAL';
							return (
								<Box>
									<Form>
										<DropDownField
											{...props}
											label="Position"
											name="position"
											placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
											options={[
												{ value: 'DIRECTOR_GENERAL', label: 'Director General', labelKh: 'អគ្គនាយក' },
												{ value: 'DEPUTY_DIRECTOR_GENERAL', label: 'Deputy Director General', labelKh: 'អគ្គនាយករង' },
												{ value: 'HEAD_OF_DEPARTMENT', label: 'Head of Department', labelKh: 'ប្រធាននាយកដ្ឋាន' },
												{ value: 'DEPUTY_HEAD_OF_DEPARTMENT', label: 'Deputy Head of Department', labelKh: 'អនុប្រធាននាយកដ្ឋាន' },
												{ value: 'HEAD_OF_OFFICE', label: 'Head of Office', labelKh: 'ប្រធានការិយាល័យ' },
												{ value: 'DEPUTY_HEAD_OF_OFFICE', label: 'Deputy Head of Office', labelKh: 'អនុប្រធានការិយាល័យ' },
												{ value: 'STATUTE_OFFICER', label: 'Statute Officer', labelKh: 'មន្រ្តីលក្ខន្តិកៈ' },
												{ value: 'INTERNSHIP_OFFICER', label: 'Internship Officer', labelKh: 'មន្រ្តីកម្មសិក្សា' },
											]}
										/>

										<DateTimeFormField
											{...props}
											label={t('Date of Last Position')}
											name="dateOfLastPosition"
											value={values?.dateOfLastPosition}
											years={true}
											nextTwoYear={false}
											required
										/>

										<DropDownField
											{...props}
											label="Rank"
											name="rank"
											placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
											options={[
												{ label: 'អនុមន្រ្តីកម្រិត១', value: 'LEVEL_7' },
												{ label: 'អនុមន្រ្តីកម្រិត២', value: 'LEVEL_6' },
												{ label: 'អនុមន្រ្តីកម្រិត៣', value: 'LEVEL_5' },
												{ label: 'នាយក្រមការ', value: 'LEVEL_4' },
												{ label: 'ក្រមការដើមខ្សែ', value: 'LEVEL_3' },
												{ label: 'ក្រមការ', value: 'LEVEL_2' },
												{ label: 'កម្មសិក្សា', value: 'LEVEL_1' },
											]}
										/>

										<DateTimeFormField
											{...props}
											label={t('Date of Last Rank')}
											name="dateOfLastRank"
											value={values?.dateOfLastRank}
											years={true}
											nextTwoYear={false}
											required
										/>

										<DropDownField
											{...props}
											label="Organisation"
											name="organisation"
											placeholder={lang === 'en' ? 'Insurance Regulator of Cambodia' : 'និយ័តករធានារ៉ាប់រងកម្ពុជា'}
											disabled={true}
										/>

										{!hideOfficeDepartment && (
											<DropDownField
												{...props}
												label="Department"
												name="department"
												placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
												options={[
													{ label: lang === 'kh' ? 'នាយកដ្ឋានកិច្ចការទូទៅ' : 'General Affairs Department', value: 'D_G_A' },
													{
														label:
															lang === 'kh'
																? 'នាយកដ្ឋានគ្រប់គ្រងអាជ្ញាបណ្ណ និងកិច្ចការគតិយុត្ត'
																: 'License and Legal Affairs Department',
														value: 'D_L_L_A',
													},
													{ label: lang === 'kh' ? 'នាយកដ្ឋានត្រួតពិនិត្យ' : 'Supervision Department', value: 'D_S' },
													{ label: lang === 'kh' ? 'នាយកដ្ឋានអភិវឌ្ឍន៍ទីផ្សារ' : 'Market Development Department', value: 'D_M_D' },
													{
														label:
															lang === 'kh'
																? 'នាយកដ្ឋានស្រាវជ្រាវ បណ្តុះបណ្តាល និងសហប្រតិបត្តិការ'
																: 'Research, Training and Cooperation Department',
														value: 'D_R_T_C',
													},
												]}
											/>
										)}

										{!hideOfficeDepartment && values?.department && (
											<DropDownField
												{...props}
												label="Office"
												name="office"
												placeholder={lang === 'en' ? 'Please select' : 'សូមជ្រើសរើស'}
												options={OFFICE_OPTIONS_BY_DEPARTMENT[values?.department] || []}
											/>
										)}

										<FileFormField {...props} label="Attachment File" name="attachmentFile" maxFileSize={c.maxFileSize / 20} />

										<Button
											id="login-btn"
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											mt={4}
											type="submit"
											borderRadius={c.borderRadius}
											borderColor="red.400"
											isLoading={loading || loadingUpdatePositionStatus}
											_focus={{ boxShadow: 'none' }}
										>
											{isEdit ? t('Update') : t('Submit')}
										</Button>
									</Form>
								</Box>
							);
						}}
					</Formik>
				</Box>
			</Box>

			{loading && <LoadingOverlayText />}
		</>
	);
}

export default PositionStatusAdd;
